import { Select as S } from 'radix-ui';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { fieldBase } from './input';
import { overlaySurface } from './menu';

type Option = { value: string; label: ReactNode; hint?: ReactNode };

export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  id,
  className,
  disabled,
  ...aria
}: {
  value: string | undefined;
  onValueChange: (v: string) => void;
  options: Option[];
  placeholder?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  return (
    <S.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <S.Trigger
        id={id}
        {...aria}
        className={cn(
          fieldBase,
          'flex h-10 min-w-0 items-center justify-between gap-2 text-left data-[placeholder]:text-fg-subtle [&>span:first-child]:min-w-0 [&>span:first-child]:truncate',
          className,
        )}
      >
        <S.Value placeholder={placeholder} />
        <S.Icon className="shrink-0">
          <ChevronDown className="size-4 text-fg-subtle" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            overlaySurface,
            'max-h-[min(20rem,var(--radix-select-content-available-height))] max-w-[calc(100vw-24px)] min-w-[min(var(--radix-select-trigger-width),calc(100vw-24px))] overflow-hidden',
          )}
        >
          <S.ScrollUpButton className="flex h-6 items-center justify-center text-fg-muted">
            <ChevronDown className="size-4 rotate-180" />
          </S.ScrollUpButton>
          <S.Viewport className="max-h-[min(18rem,var(--radix-select-content-available-height))] overflow-y-auto">
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                className="flex min-h-9 cursor-default items-center gap-2 rounded-[9px] py-1.5 pr-8 pl-2.5 text-[13.5px] outline-none data-[highlighted]:bg-hover relative"
              >
                <div className="min-w-0 break-words">
                  <S.ItemText>{o.label}</S.ItemText>
                  {o.hint && <div className="text-[12px] text-fg-subtle">{o.hint}</div>}
                </div>
                <S.ItemIndicator className="absolute right-2.5">
                  <Check className="size-4 text-signal" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
          <S.ScrollDownButton className="flex h-6 items-center justify-center text-fg-muted">
            <ChevronDown className="size-4" />
          </S.ScrollDownButton>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}
